import React, { useRef, useEffect, useState, useImperativeHandle } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const StochasticChart = React.forwardRef(({ paths }, ref) => {
    const chartRef = useRef(null);
    useImperativeHandle(ref, () => chartRef.current, [chartRef.current]);
    const [chartData, setChartData] = useState({ datasets: [] });

    useEffect(() => {
        const chart = chartRef.current;

        if (!chart || !paths) return;

        const ctx = chart.ctx;

        // Create gradients
        const gradient95 = ctx.createLinearGradient(0, 0, 0, 400);
        gradient95.addColorStop(0, 'rgba(0, 51, 102, 0.1)');
        gradient95.addColorStop(1, 'rgba(0, 51, 102, 0.0)');

        const gradient90 = ctx.createLinearGradient(0, 0, 0, 400);
        gradient90.addColorStop(0, 'rgba(0, 51, 102, 0.2)');
        gradient90.addColorStop(1, 'rgba(0, 51, 102, 0.0)');

        const data = {
            labels: paths.years,
            datasets: [
                {
                    label: '95th Percentile',
                    data: paths.p95,
                    borderColor: 'transparent',
                    backgroundColor: gradient95,
                    fill: '+4',
                    pointRadius: 0,
                    tension: 0.4,
                },
                {
                    label: '90th Percentile',
                    data: paths.p90,
                    borderColor: 'transparent',
                    backgroundColor: gradient90,
                    fill: '+2',
                    pointRadius: 0,
                    tension: 0.4,
                },
                {
                    label: 'Median (50th)',
                    data: paths.p50,
                    borderColor: '#072040',
                    borderWidth: 2,
                    pointRadius: 0,
                    tension: 0.4,
                    fill: false,
                },
                {
                    label: '10th Percentile',
                    data: paths.p10,
                    borderColor: 'transparent',
                    fill: false,
                    pointRadius: 0,
                    tension: 0.4,
                },
                {
                    label: '5th Percentile',
                    data: paths.p5,
                    borderColor: 'transparent',
                    fill: false,
                    pointRadius: 0,
                    tension: 0.4,
                },
            ],
        };

        setChartData(data);
    }, [paths]);

    if (!paths) return null;

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: {
                labels: { usePointStyle: true, boxWidth: 6 },
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        return context.dataset.label + ': ' + context.parsed.y.toFixed(0) + '%';
                    },
                },
            },
        },
        scales: {
            y: {
                beginAtZero: false,
                grid: { color: 'rgba(0, 0, 0, 0.05)' },
                title: { display: true, text: 'Cumulative Return (%)' },
            },
            x: {
                grid: { display: false },
                title: { display: true, text: 'Years' },
            },
        },
    };

    return <Line ref={chartRef} data={chartData} options={options} />;
});

export default StochasticChart;
